/**
 * Purchase-flow rules shared by client and server (pure):
 *  - Ask-to-Buy / deferred purchases: a child's purchase may wait for a parent's approval (App Store
 *    "Ask to Buy", Play "pending" transactions). The app shows a "waiting for a grown-up" state and
 *    must not grant access until the store confirms.
 *  - Managed / supervised devices (school or company MDM): the app is not offered there, so the
 *    client shows a block screen; devices where in-app purchases are switched off see a softer
 *    "purchases are turned off" notice on the paywall.
 */
import type { BillingPeriod, PlanId } from './constants';

/** Apple lets a parent answer an Ask-to-Buy request for 24 hours (we keep the overlay a bit longer). */
export const PENDING_PURCHASE_TTL_HOURS = 72;

export interface PendingPurchase {
  plan: PlanId;
  period: BillingPeriod;
  /** ISO time the request was made. */
  since: string;
  /** Where it came from: 'app_store' | 'play_store' | 'mock'. */
  store: string;
}

export type PendingResolution = 'approved' | 'declined';

export type PendingStatus = 'waiting' | 'expired';

export function pendingStatus(p: PendingPurchase, now: Date): PendingStatus {
  const age = now.getTime() - new Date(p.since).getTime();
  return age > PENDING_PURCHASE_TTL_HOURS * 3600_000 ? 'expired' : 'waiting';
}

/**
 * Reconcile a pending purchase against the latest entitlement: an active paid tier means the parent
 * approved; an expired request is dropped; otherwise keep waiting.
 */
export function reconcilePending(
  p: PendingPurchase | null,
  entitlementTier: 'free' | PlanId,
  now: Date,
): { pending: PendingPurchase | null; resolution: PendingResolution | 'expired' | null } {
  if (!p) return { pending: null, resolution: null };
  if (entitlementTier !== 'free') return { pending: null, resolution: 'approved' };
  if (pendingStatus(p, now) === 'expired') return { pending: null, resolution: 'expired' };
  return { pending: p, resolution: null };
}

/* ------------------------------- Device policy ------------------------------- */

export interface DevicePolicy {
  /** Enrolled in device management (MDM / Android Enterprise work profile or device owner). */
  managed: boolean;
  /** iOS supervised device (school / company). */
  supervised: boolean;
  /** Store purchases allowed (Screen Time / parental controls may switch them off). */
  purchasesAllowed: boolean;
}

export const UNMANAGED_DEVICE: DevicePolicy = { managed: false, supervised: false, purchasesAllowed: true };

export type DeviceGate = 'ok' | 'purchasesOff' | 'blocked';

/**
 * 'blocked'      managed or supervised device and the live config blocks those (full-screen block);
 * 'purchasesOff' purchases restricted: paywall shows "ask a grown-up to allow purchases";
 * 'ok'           everything allowed.
 */
export function deviceGate(policy: DevicePolicy, cfg: { blockManagedDevices: boolean }): DeviceGate {
  if (cfg.blockManagedDevices && (policy.managed || policy.supervised)) return 'blocked';
  if (!policy.purchasesAllowed) return 'purchasesOff';
  return 'ok';
}

/* ------------------------------ Invites and gifts (§7.2, §7.4) ------------------------------ */

/** Readable code alphabet: no 0/O, 1/I/L, so codes survive being read aloud or typed by a grown-up. */
export const CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const REFERRAL_CODE_LENGTH = 6;
/** Inviter reward per invitee whose first paid period was collected. */
export const REFERRAL_REWARD_DAYS = 30;
/** Max rewarded invites per inviter in a rolling 365 days. */
export const REFERRAL_REWARD_CAP = 12;
/** A gift grants one year and must be redeemed within 12 months of purchase. */
export const GIFT_GRANT_DAYS = 365;
export const GIFT_EXPIRY_DAYS = 365;
export const GIFT_PRODUCTS = {
  family_yearly: { plan: 'family', period: 'yearly' },
  plus_yearly: { plan: 'individual', period: 'yearly' },
} as const satisfies Record<string, { plan: PlanId; period: BillingPeriod }>;
export type GiftProduct = keyof typeof GIFT_PRODUCTS;

/** Code from random bytes (any source: crypto on the server, tests pass fixed bytes). */
export function codeFromBytes(bytes: ArrayLike<number>, length: number): string {
  let out = '';
  for (let i = 0; i < length; i++) out += CODE_ALPHABET[(bytes[i % bytes.length] ?? 0) % CODE_ALPHABET.length];
  return out;
}

const cleanCode = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '');
const inAlphabet = (s: string) => [...s].every((c) => CODE_ALPHABET.includes(c));

/** "k7q-2mx " → "K7Q2MX"; null when it cannot be a referral code. */
export function normalizeReferralCode(s: string): string | null {
  const c = cleanCode(s);
  return c.length === REFERRAL_CODE_LENGTH && inAlphabet(c) ? c : null;
}

/** "gift abcd efgh" / "GIFT-ABCD-EFGH" → "GIFT-ABCD-EFGH"; null when malformed. */
export function normalizeGiftCode(s: string): string | null {
  let c = cleanCode(s);
  if (c.startsWith('GIFT')) c = c.slice(4);
  return c.length === 8 && inAlphabet(c) ? `GIFT-${c.slice(0, 4)}-${c.slice(4)}` : null;
}

export const giftCodeFromBytes = (bytes: ArrayLike<number>) => {
  const c = codeFromBytes(bytes, 8);
  return `GIFT-${c.slice(0, 4)}-${c.slice(4)}`;
};
